<template>
    <div class="module">
        <header class="module__header">
            <a
                class="back-btn"
                @click="goBack('admin-users')"
            >
                <span class="icon-chevron-left" />
            </a>
            <h1 class="has-back-btn">Edit User</h1>
        </header>

        <section class="module__actions">
            <button
                class="btn"
                :disabled="resetPasswordLinkSent"
                @click="sendResetPasswordLink"
            >
                Send Reset Password Link
            </button>
        </section>

        <section class="module__body">
            <form
                v-if="formLoaded"
                class="full-width"
                @submit.prevent="updateUser"
            >
                <div class="form-flex">
                    <div class="form-flex-group">
                        <div class="form-group">
                            <label for="name">Name</label>
                            <input
                                id="name"
                                v-model="name"
                                type="text"
                                required
                                maxlength="60"
                            />
                        </div>

                        <div class="form-group">
                            <label for="title">Title</label>
                            <input
                                id="title"
                                v-model="title"
                                type="text"
                                required
                                maxlength="60"
                            />
                        </div>

                        <div class="form-group">
                            <label for="username">Username</label>
                            <input
                                id="username"
                                v-model="username"
                                type="text"
                                required
                                maxlength="32"
                            />
                        </div>
                    </div>

                    <div class="form-flex-group">
                        <div class="form-group">
                            <slider-toggle-field-container title="Permissions">
                                <slider-toggle-field
                                    v-if="organizationHasLicenses"
                                    description="Access to premium mobile features"
                                    tooltip="Access to premium mobile features. Requires a software license."
                                    label="Premium User"
                                    name="is_premium"
                                    :value="is_premium"
                                    @update="is_premium = $event"
                                />
                                <slider-toggle-field
                                    title="Permissions"
                                    description="Access to Mission Control backhaul map"
                                    label="Mission Control User"
                                    name="permissions"
                                    :value="missionControlUser"
                                    @update="missionControlUser = $event"
                                />
                                <slider-toggle-field
                                    description="Access to App Repository"
                                    label="App Repository"
                                    name="permissions"
                                    :value="appRepositoryUser"
                                    @update="appRepositoryUser = $event"
                                />
                                <slider-toggle-field
                                    description="Access to Relays &amp; Relay Health Checks"
                                    label="Relay Manager"
                                    name="permissions"
                                    :value="relayManagerUser"
                                    @update="relayManagerUser = $event"
                                />
                            </slider-toggle-field-container>
                        </div>
                        <div class="form-group">
                            <label for="groups">Add to Groups</label>
                            <ul v-if="allGroups.length">
                                <li
                                    v-for="group in allGroups"
                                    :key="group.id"
                                >
                                    <input
                                        :id="group.id"
                                        v-model="userGroupIds"
                                        name="groups"
                                        type="checkbox"
                                        :value="group.id"
                                    />
                                    <label :for="group.id">{{ group.name }}</label>
                                </li>
                            </ul>
                            <p v-else>
                                <span>No groups available.</span>
                                <router-link
                                    :to="`/${organizationSlug}/user-groups/create`"
                                    tag="a"
                                    class="btn btn-small"
                                >
                                    Create a Group
                                </router-link>
                            </p>
                        </div>
                    </div>
                </div>

                <form-errors :response="errorResponse" />

                <div class="form-group">
                    <button
                        type="submit"
                        class="btn btn-primary"
                        :disabled="!formLoaded"
                        :class="{ active: submitted }"
                    >
                        <div>Update</div>
                    </button>
                </div>
            </form>
        </section>
    </div>
</template>

<script>
import FormErrors from '@/components/FormErrors.vue'
import SliderToggleFieldContainer from '@/components/SliderToggleFieldContainer.vue'
import SliderToggleField from '@/components/SliderToggleField.vue'
import canToast from '@/mixins/canToast'
import hasBackButton from '@/mixins/hasBackButton'
import needsOrganizationSlug from '@/mixins/needsOrganizationSlug'
import fetchLicenseStats from '@/store/modules/licenses/FetchLicenseStats'
import confirmLicenseOverage from './ConfirmLicenseOverage.js'

export default {
    components: {
        FormErrors,
        SliderToggleField,
        SliderToggleFieldContainer,
    },

    mixins: [canToast, hasBackButton, needsOrganizationSlug],

    data() {
        return {
            id: this.$route.params.id,
            name: null,
            email: null,
            title: null,
            username: null,
            is_premium: false,
            original_is_premium: false,
            allGroups: [],
            userGroupIds: [],
            errorResponse: null,
            submitted: false,
            resetPasswordLinkSent: false,
            missionControlUser: false,
            appRepositoryUser: false,
            relayManagerUser: false,
            licensesPurchased: 0,
            licensesUsed: 0,
            licensesHardCap: 0,
            formLoaded: false,
        }
    },

    computed: {
        organizationHasLicenses() {
            return this.licensesPurchased > 0
        },
    },

    mounted() {
        const userRequest = this.$http.get(`/${this.organizationSlug}/users/${this.id}`).then(({ data }) => {
            const { user } = data
            this.name = user.name
            this.title = user.title
            this.email = user.email
            this.username = user.username
            this.is_premium = Boolean(user.is_premium)
            this.original_is_premium = Boolean(user.is_premium)
            this.userGroupIds = user.groups.map((group) => group.id)
            this.allGroups = data.allGroups
            this.missionControlUser = user.role_names.indexOf('mission_control_user') !== -1
            this.appRepositoryUser = user.role_names.indexOf('app_repository_user') !== -1
            this.relayManagerUser = user.role_names.indexOf('relay_manager_user') !== -1
        })
        const licensesRequest = fetchLicenseStats(this.organizationSlug, (purchasedCount, usedCount, hardCap) => {
            this.licensesPurchased = purchasedCount
            this.licensesUsed = usedCount
            this.licensesHardCap = hardCap
        })

        Promise.all([userRequest, licensesRequest]).then(() => {
            this.formLoaded = true
        })
    },

    methods: {
        updateUser() {
            if (this.isExceedingSoftCap()) {
                confirmLicenseOverage(this.licensesPurchased, this.licensesHardCap)
                    .then(() => this.saveUserUpdate())
                    .catch(() => {})
            } else {
                this.saveUserUpdate()
            }
        },

        saveUserUpdate() {
            this.submitted = true

            this.$http
                .put(`/${this.organizationSlug}/users/${this.id}`, {
                    name: this.name,
                    title: this.title,
                    username: this.username,
                    is_premium: Number(this.is_premium),
                    groups: this.userGroupIds,
                    permissions: this.selectedPermissions,
                    mission_control_user: this.missionControlUser,
                    app_repository_user: this.appRepositoryUser,
                    relay_manager_user: this.relayManagerUser,
                })
                .then(() => {
                    this.toast('User successfully updated!')
                    this.$router.push(`/${this.organizationSlug}/users`)
                })
                .catch((error) => {
                    this.errorResponse = error.response
                    this.submitted = false
                })
        },

        sendResetPasswordLink() {
            this.$http
                .post('auth/send-password-reset-link', {
                    email: this.email,
                })
                .then(() => {
                    this.toast(`Reset password link sent to ${this.email}`)
                    this.resetPasswordLinkSent = true
                })
        },

        isExceedingSoftCap() {
            const isEnablingPremium = this.is_premium && !this.original_is_premium
            return (
                isEnablingPremium &&
                this.licensesUsed >= this.licensesPurchased &&
                this.licensesUsed < this.licensesHardCap
            )
        },
    },
}
</script>
